/**
 * PROJECT-080 / PROJECT-090 — Investor Outreach FTUE API
 *
 * First-time user experience for founder-led investor outreach:
 *   1. Wizard collects raise profile.
 *   2. Backend ranks investor candidates from imported BD90.6 kernels
 *      (direct /candidates read — D-052: never the n8n process for interactive reads).
 *   3. User previews the ask-for-advice email (dry-run) with the per-investor
 *      compliance gate result (D-044 overlays).
 *   4. Dry-run "Send" assembles the campaign but does NOT trigger Listmonk.
 */

import AuthUtils from "@/providers/firebase/authUtils";
import { ApiResponse } from "./kernelBotApi";

const API_URL = import.meta.env.VITE_API_URL || "http://localhost:8000";

const DEFAULT_TIMEOUT_MS = 30_000;

/**
 * Turn a FastAPI-native refusal into a sentence a founder can act on.
 *
 * Every caller of this module reads the BE's envelope (`status` / `message`) and falls back to a
 * generic sentence when either is absent. A refusal that never took the envelope shape —
 * `{"detail": ...}`, i.e. a 422 validation error, a 401, a 403 — therefore arrived as
 * "Failed to assemble preview." with the server's own reason discarded. Measured on DEV
 * 2026-09-21: the Assemble button posted a sender with no `raising`, the BE answered 422 with
 * `loc: [body, sender, raising]`, and the founder was told nothing.
 */
function refusalMessage(body: unknown, status: number): string {
  const detail = (body as { detail?: unknown } | null)?.detail;
  if (typeof detail === "string" && detail.trim()) return detail;
  if (Array.isArray(detail)) {
    const fields = detail
      .map((d) =>
        Array.isArray((d as { loc?: unknown[] })?.loc)
          ? (d as { loc: unknown[] }).loc.filter((p) => p !== "body" && p !== "sender").join(" ")
          : "",
      )
      .filter(Boolean);
    if (fields.length > 0) {
      return `Not ready yet — Raisy needs ${[...new Set(fields)].join(", ")}.`;
    }
  }
  return `Not ready yet — the server answered HTTP ${status}.`;
}

async function apiCall<T>(
  path: string,
  options: RequestInit & { timeoutMs?: number } = {},
): Promise<ApiResponse<T>> {
  const { timeoutMs = DEFAULT_TIMEOUT_MS, ...fetchOptions } = options;
  const headers = await AuthUtils.getAuthHeaders();
  // The same trap, closed at the class: ANY future multipart caller of this helper would
  // otherwise be sent as `application/json` and arrive without its file.
  if (fetchOptions.body instanceof FormData) {
    delete (headers as Record<string, string>)["Content-Type"];
  }
  const controller = new AbortController();
  const timeoutId = window.setTimeout(() => controller.abort(), timeoutMs);
  try {
    const res = await fetch(`${API_URL}${path}`, {
      ...fetchOptions,
      headers: { ...headers, ...fetchOptions.headers },
      signal: controller.signal,
    });
    // A body that is not this API's envelope is normalised, never forwarded raw: every caller
    // only understands `status` / `message`, so an un-normalised refusal silently became its
    // generic fallback. (`outreachLedgerApi`'s wrapper already holds this shape.)
    const body: unknown = await res.json().catch(() => null);
    if (body && typeof body === "object" && "status" in body) {
      return body as ApiResponse<T>;
    }
    return {
      status: false,
      statusCode: res.status,
      message: refusalMessage(body, res.status),
      data: null,
    };
  } finally {
    window.clearTimeout(timeoutId);
  }
}

/**
 * D-074 raise-profile SSOT shape — the declared round persisted for a company.
 * Fire-and-forget on write; GET /investor-outreach/raise-profile restores it.
 */
export interface RaiseProfile {
  raising_stage?: string;
  raising_amount?: string;
  check_size?: string;
  sector?: string;
  target_geography?: string;
  region?: string;
  country?: string;
  /** @P279 T0401 (D-279-004) — where investors book a call. The founder's own link, stored in the
   *  per-user preferences bag beside the raise fields, because it is a lighter version of the
   *  calendar CONNECTION `@P133` T1311 already rules "per entity/mailbox". `null` = none set. */
  booking_link?: string | null;
  /** D-085 — when this round was first declared (ISO). The Running-raise card shows it as the
   *  raise's start date; preserved across later geo/sector updates. */
  declared_at?: string;
}

/**
 * Persist the (possibly user-adjusted) raise profile back onto the entity, so the
 * next visit prefills the user's own choice rather than only the stage benchmark.
 * Stored server-side as the BD90 "Raise Profile Runtime Data" kernel + a mirror in
 * preferences[user_id] (via /api/v1/entity/update, D-074). FIRE-AND-FORGET — callers
 * never await it; the funnel read path must never depend on this write succeeding.
 */
export async function updateEntityRaiseProfile(
  entityId: string,
  profile: RaiseProfile,
): Promise<ApiResponse<unknown>> {
  return apiCall("/api/v1/entity/update", {
    method: "POST",
    body: JSON.stringify({ entity_id: entityId, ...profile }),
  });
}

/**
 * Restore the saved raise round for a company (D-074). Reads the entity's newest
 * BD90 "Raise Profile Runtime Data" kernel; returns data:{} when nothing declared.
 * Fire-and-forget style read — never block the UI on it.
 */
export async function getRaiseProfile(entityId: string): Promise<ApiResponse<RaiseProfile>> {
  return apiCall(`/api/v1/investor-outreach/raise-profile?entity_id=${encodeURIComponent(entityId)}`, {
    timeoutMs: 15_000,
  });
}

/**
 * MR23 / D-092 — the founder's campaign spine (one row per goal-scoped engagement: a raise is an
 * `equity` campaign). Owner-scoped list across every entity the caller can access. Each row carries
 * the spine (campaignkey/campaign_id, pipeline, status, round_seq, declared_at) + the per-campaign
 * BD90 raise-profile kernel (`raise_profile`) + honest per-campaign `reachouts`
 * (communications.campaignid, MR20). A row exists once a round is declared through the deployed
 * wiring (migration 127).
 */
export interface CampaignRow {
  campaign_id: string;
  campaignkey: string;
  campaign_number: number;
  entity_id: string;
  pipeline?: string;
  status?: string;
  round_seq?: number | null;
  declared_at?: string | null;
  started_at?: string | null;
  closed_at?: string | null;
  kernel_keys_ref?: string[];
  /** @P279 T0200 — the raise's deck, an id into `uploaded_sources`; null when none is attached. */
  deck_source_id?: string | null;
  created_at?: string;
  updated_at?: string;
  entity_name?: string | null;
  /** Honest per-campaign send count (communications rows with campaignid === campaignkey). */
  reachouts?: number;
  /** The per-campaign BD90 raise-profile kernel payload (stage/amount/sector/geo/declared_at). */
  raise_profile?: RaiseProfile | Record<string, never>;
}

export interface CampaignListResponse {
  campaigns: CampaignRow[];
  entity_count: number;
}

export async function getCampaigns(): Promise<ApiResponse<CampaignListResponse>> {
  return apiCall("/api/v1/investor-outreach/campaigns", { timeoutMs: 15_000 });
}

// ── Types ──────────────────────────────────────────────────────────

export interface InvestorCandidate {
  entity_id: string;
  entitykey: string;
  name: string;
  fund: string;
  focus: string;
  stage_focus: string;
  country: string;
  region: string;
  city: string;
  check_size: string;
  email: string;
  email_status: string;
  data_quality_score: number;
  relationship_id: string | null;
  bd70_stage: string;
  website: string;
  // D-109 — LLM/Hunter-collected profile facts (stored in the DB but previously never sent).
  description: string;
  linkedin: string;
  contact_name: string;
  recipient_country: string;
  fit_score: number;
  signals: {
    stage_match: number;
    sector_match: number;
    geography_match: number;
    check_size_match: number;
    data_quality: number;
  };
  weights: Record<string, number>;
  /**
   * D-129 — how many of the four match axes have a value on the INVESTOR's side (0–4), and
   * which ones. The match score is computed over those axes only, because an axis we do not
   * know is unknown and never a mismatch — the same law the axis columns already obey
   * ("an axis without source data is NOT a 0% match"). Absent on an older BE; when it is
   * missing the surface shows the score alone rather than inventing a coverage.
   */
  coverage?: number;
  coverage_axes?: string[];
  // D-118 — trust receipts: which source produced each enriched fact + when research last
  // ran + email/hunter verification. Additive — an older BE omits it; the FE renders
  // coverage/provenance only when present and never fabricates a source.
  provenance?: {
    sources?: Record<string, string>;
    research_run_at?: string;
    email_source?: string;
    best_email_status?: string;
    hunter_status?: string;
    data_quality?: number;
  };
}

export interface CandidateListResponse {
  owner_entity_id: string | null;
  company_profile: {
    raising_stage: string;
    sector: string;
    target_geography: string;
    raising_amount: string;
  };
  total_available: number;
  candidates: InvestorCandidate[];
  funnel?: {
    pool: number;
    after_geography: number;
    after_filters: number;
    ranked: number;
  };
}

export interface InvestorPoolStats {
  total: number;
  by_region: Record<string, number>;
  by_country: Record<string, number>;
}

export interface SenderInfo {
  company: string;
  pitch: string;
  raising: string;
  name: string;
  title?: string;
  email: string;
  from_address?: string;
  reply_to?: string;
  unsubscribe_link: string;
  physical_address: string;
  recipient_country?: string;
  consent_basis?: string;
  legitimate_interest_assessment?: string;
  content_class?: string;
  extra_fields?: Record<string, string>;
}

export interface OutreachSendRequest {
  /** Optional in global mode — explicit investor_entity_ids selects entities directly (D-045). */
  owner_entity_id?: string | null;
  sender: SenderInfo;
  investor_entity_ids?: string[];
  template_key?: string;
  campaign_id?: string;
  campaign_name?: string;
  limit?: number;
  dry_run?: boolean;
  /** T0207 — "Bcc myself": send the founder a copy of every email actually sent.
   *  Not a second reachout — no credit is used and no ledger row is written for the copy. */
  bcc_self?: boolean;
  /** BD pipeline code selecting the compliance overlay (default BD70.3). */
  use_case?: string;
  /** D-086 slice 2 (O-A): when true, each recipient email is drafted per-investor by the
   *  registered Email Writer bot (founder-reviewed sender wording + investor BD90 facts);
   *  deterministic template-fill is the fallback on any writer failure. */
  personalize?: boolean;
  /**
   * PO 2026-09-21 — the raising company these drafts BELONG to. It scopes the logged writer
   * calls (never their kernel assembly), so each per-investor draft appears in that
   * workspace's prepared queue — the Drafts folder of `/email` — instead of being an
   * unscoped row any authenticated caller can read. Deliberately not `owner_entity_id`:
   * that one switches the send into legacy relationship mode.
   */
  drafts_entity_id?: string | null;
}

export interface ComplianceInfo {
  allowed: boolean;
  country_code: string;
  policy?: Record<string, unknown>;
  missing_fields: string[];
  consent_basis_required: boolean;
  message: string;
  use_case?: string | null;
  advisories: string[];
}

export type DryRunStatus = "dry_run_ready" | "dry_run_blocked" | "dry_run_no_email";

export interface OutreachResult {
  investor_entity_id: string;
  investor_name: string;
  investor_email: string;
  fit_score?: number;
  communication_id: string | null;
  /**
   * P133 D-133-023 — the `bot_call_logs` row this investor's draft was written by.
   *
   * WHY IT IS ON THE ROW. The draft the board previews IS a call the writer made, and that call
   * is what holds the item's review, its versions and its decision. Without the id, a host can
   * only render a second, read-only copy of the same draft — the duplication D-133-023 forbids.
   * With it, the host opens the SAME review `/email` opens, on the SAME item.
   *
   * Null is honest and expected in two cases: the draft came from the deterministic template
   * (no writer call was made), or the writer fell back after a failure. A surface must say so
   * rather than offer a review there is nothing to review.
   */
  draft_call_id?: string | null;
  /**
   * Which writer bot produced the draft above. A campaign HOST uses it to scope the shared queue
   * to its own drafts (`bot/call-history` accepts a `botKey` filter) rather than rendering the
   * workspace's entire undecided queue, which also holds drafts written for other purposes.
   * Null when the draft came from the deterministic template — no writer ran. See `draft_call_id`.
   */
  draft_bot_key?: string | null;
  status: "sent" | "failed" | "dry_run" | DryRunStatus | string;
  error?: string | null;
  compliance?: ComplianceInfo;
  subject?: string;
  body?: string;
  /** D-086 slice 2 — true when this draft was AI-written per-investor (vs template-fill). */
  personalized?: boolean;
}

export interface OutreachSendResponse {
  results: OutreachResult[];
  sent_count?: number;
  failed_count?: number;
  dry_run?: boolean;
  ready_count?: number;
  blocked_count?: number;
}

// ── Endpoints ──────────────────────────────────────────────────────

export async function getInvestorCandidates(
  profile: {
    raising_stage?: string;
    sector?: string;
    target_geography?: string;
    region?: string;
    country?: string;
    raising_amount?: string;
    limit?: number;
    min_email_status?: string;
  } = {},
  owner_entity_id?: string | null,
): Promise<ApiResponse<CandidateListResponse>> {
  const params = new URLSearchParams();
  if (owner_entity_id) params.set("owner_entity_id", owner_entity_id);
  if (profile.raising_stage) params.set("raising_stage", profile.raising_stage);
  if (profile.sector) params.set("sector", profile.sector);
  if (profile.target_geography) params.set("target_geography", profile.target_geography);
  if (profile.region) params.set("region", profile.region);
  if (profile.country) params.set("country", profile.country);
  if (profile.raising_amount) params.set("raising_amount", profile.raising_amount);
  if (profile.limit !== undefined) params.set("limit", String(profile.limit));
  if (profile.min_email_status) params.set("min_email_status", profile.min_email_status);

  return apiCall(`/api/v1/investor-outreach/candidates?${params.toString()}`, {
    timeoutMs: 45_000,
  });
}

export async function getInvestorPoolStats(): Promise<ApiResponse<InvestorPoolStats>> {
  return apiCall("/api/v1/investor-outreach/pool-stats", {
    timeoutMs: 30_000,
  });
}

// ── MR26 / D-116 — centralised raise scores (coverage + engagement heat) ─────

export interface RaiseScore {
  /** 0–100 (null when not computable). */
  score: number | null;
  /** Backend-derived band (MR26: the FE never re-derives thresholds). */
  band: string | null;
  /** Plain-language explanation returned by the same backend call (tooltip verbatim). */
  methodology_string: string;
}

export interface CoverageScore extends RaiseScore {
  pool: number;
  after_geography: number;
  reach: number;
}

export interface EngagementScore extends RaiseScore {
  contacted: number;
  hot: number;
  warm: number;
  cold: number;
  last_touch: string | null;
}

export interface RaiseScoresResponse {
  entity_id: string;
  campaignkey: string | null;
  coverage: CoverageScore;
  engagement: EngagementScore;
}

/**
 * MR26 / D-116 — ONE backend call returns both measures (coverage + engagement)
 * and their plain-language methodology strings. The FE renders the payload and
 * never derives bands/heat itself.
 */
export async function getRaiseScores(
  entityId: string,
  campaignkey?: string | null,
): Promise<ApiResponse<RaiseScoresResponse>> {
  const params = new URLSearchParams({ entity_id: entityId });
  if (campaignkey) params.set("campaignkey", campaignkey);
  return apiCall(`/api/v1/investor-outreach/scores?${params.toString()}`, {
    timeoutMs: 45_000,
  });
}

/**
 * project092 / T0209 — outreach analytics over the `communications` ledger.
 *
 * `reply_rate` / `bounce_rate` are PERCENTAGES and are `null` when nothing was sent — never
 * `0`, because `0%` would read as "nobody replied" when the truth is "nothing was sent".
 * `opens`, `clicks` and `meetings` are always `null` today: no producer writes them
 * (`D-004`, 2026-10-04), and `not_measured` carries the reason in words for each — the FE
 * shows that sentence rather than inventing a number.
 */
export interface OutreachAnalyticsCampaign {
  campaign_id: string;
  campaign_name: string;
  reachouts: number;
  replies: number;
  bounces: number;
  reply_rate: number | null;
  bounce_rate: number | null;
}

export interface OutreachAnalyticsResponse {
  entity_id: string;
  campaignkey: string | null;
  window: { start: string | null; end: string | null; bucket_basis: string };
  reachouts: number;
  replies: number;
  bounces: number;
  /** T0220 — sends whose link an investor followed, recorded by the click redirect. */
  clicks: number;
  /** T0221 — meetings receipted against the send they came from. */
  meetings: number;
  failed: number;
  reply_rate: number | null;
  bounce_rate: number | null;
  click_rate: number | null;
  meeting_rate: number | null;
  /** Always `null`: an open needs an HTML pixel and the send is plain text by policy. */
  opens: null;
  by_campaign: OutreachAnalyticsCampaign[];
  not_measured: Record<string, string>;
  methodology_string: string;
}

export async function getOutreachAnalytics(
  entityId: string,
  opts: { campaignkey?: string | null; startDate?: string; endDate?: string } = {},
): Promise<ApiResponse<OutreachAnalyticsResponse>> {
  const params = new URLSearchParams({ entity_id: entityId });
  if (opts.campaignkey) params.set("campaignkey", opts.campaignkey);
  if (opts.startDate) params.set("start_date", opts.startDate);
  if (opts.endDate) params.set("end_date", opts.endDate);
  return apiCall(`/api/v1/investor-outreach/analytics?${params.toString()}`, {
    timeoutMs: 45_000,
  });
}

/**
 * project092 / T0217 — one investor's real history with one raising company.
 *
 * `ladder` is the engagement ladder (`sent → delivered → opened → clicked → replied →
 * meeting booked`) with each rung marked `measured`. A rung with `measured: false` has NO
 * producer in the backend (`D-004`), so the surface must say *"not tracked yet"* instead of
 * rendering it as unreached — the same honesty rule the round-level dashboard follows.
 */
export interface InvestorActivityRung {
  stage: string;
  label: string;
  measured: boolean;
  reached: boolean;
  at: string | null;
}

export interface InvestorActivityEvent {
  at: string | null;
  kind: string;
  label: string;
  channel: string;
  direction: string;
  subject: string | null;
  status: string | null;
}

export interface InvestorActivityResponse {
  owner_entity_id: string;
  investor_entity_id: string;
  ladder: InvestorActivityRung[];
  events: InvestorActivityEvent[];
  event_count: number;
  furthest: string | null;
  not_measured: Record<string, string>;
}

/**
 * project092 / T0204 — the founder's own email templates.
 *
 * `custom` marks a template this company saved itself; `own` says it belongs to the caller.
 * The list is the platform's templates PLUS this company's own — never another company's.
 */
export interface OutreachTemplate {
  kernel_key: string;
  name: string;
  subject: string;
  body: string;
  custom: boolean;
  own: boolean;
  /**
   * `IN-28`'s tooltip line, served from `kernel.description`.
   *
   * Present and nullable on purpose: every seed migration (110-117) left the column NULL, so this
   * is the field to DISPLAY and not yet a field with content. A surface must render null as "no
   * description yet" — never as the text "null", and never by hiding the pill.
   */
  description: string | null;
}

/**
 * Which of the company's templates it offers, and where that answer came from.
 *
 * `selected: null` and `selected: []` are DIFFERENT answers and must never be collapsed:
 *   - `null` — the company never chose, so the whole catalogue applies (`default: true`).
 *   - `[]`   — the company chose, and it chose to offer none.
 */
export interface OutreachTemplateSelection {
  entity_id: string;
  selected: string[] | null;
  /** True when `selected` is null because nobody has chosen — the catalogue applies as-is. */
  default: boolean;
  scope: "entity";
  updated_by: string | null;
  updated_at: string | null;
}

export interface OutreachTemplateList {
  templates: OutreachTemplate[];
  custom_count: number;
  /** The `[placeholder]` tokens Raisy can fill — shown as a cheat-sheet, never invented. */
  tokens: string[];
  /**
   * Rides the SAME call as the list, so one round trip answers the whole surface: the catalogue,
   * each pill's tooltip, which pills are on, and whether that came from the company or the default
   * (`D-135-051`: the read returns the value's SOURCE, so a stale screen is recognisable).
   */
  selection: OutreachTemplateSelection;
}

export async function listOutreachTemplates(
  entityId: string,
): Promise<ApiResponse<OutreachTemplateList>> {
  const params = new URLSearchParams({ entity_id: entityId });
  return apiCall(`/api/v1/investor-outreach/templates?${params.toString()}`, { timeoutMs: 30_000 });
}

/** `IN-28` — read the company's choice on its own, when the list is already on screen. */
export async function getOutreachTemplateSelection(
  entityId: string,
): Promise<ApiResponse<OutreachTemplateSelection>> {
  const params = new URLSearchParams({ entity_id: entityId });
  return apiCall(`/api/v1/investor-outreach/templates/selection?${params.toString()}`, {
    timeoutMs: 30_000,
  });
}

/**
 * `IN-28` — set which of its templates the company offers.
 *
 * `PUT`, not `POST`: this replaces the whole selection, so a caller that sends a stale list
 * overwrites rather than appends. A key the company may not send with is refused by the backend
 * with a 400 NAMING the key, and NOTHING is written — so a failed save leaves the previous choice
 * exactly as it was, and the caller should surface the message rather than assume it saved.
 *
 * "Apply to all companies" is this called once per company by the caller that shows the blast
 * radius; there is deliberately no bulk endpoint (see the BE route's own note).
 */
export async function saveOutreachTemplateSelection(
  entityId: string,
  selected: string[],
): Promise<ApiResponse<OutreachTemplateSelection>> {
  return apiCall("/api/v1/investor-outreach/templates/selection", {
    method: "PUT",
    body: JSON.stringify({ entity_id: entityId, selected }),
  });
}

export async function saveOutreachTemplate(
  entityId: string,
  name: string,
  subject: string,
  body: string,
): Promise<ApiResponse<{ kernel_key: string; name: string; version: number }>> {
  return apiCall("/api/v1/investor-outreach/template/save", {
    method: "POST",
    body: JSON.stringify({ entity_id: entityId, name, subject, body }),
  });
}

/**
 * project092 / T0211 — pitch-deck attachments.
 *
 * Upload is a separate fetch from `apiCall` on purpose: `apiCall` always sets
 * `Content-Type: application/json`, and a multipart body must let the browser set its own
 * boundary. Sending the wrong content type is why a file upload silently arrives as JSON.
 */
export interface OutreachAttachment {
  id: string;
  filename: string;
  mime_type: string | null;
  size_bytes: number | null;
}

export interface OutreachAttachmentList {
  attachments: OutreachAttachment[];
  allowed_mime: string[];
  max_bytes: number;
  max_per_send: number;
}

export async function uploadOutreachAttachment(
  file: File,
): Promise<ApiResponse<OutreachAttachment>> {
  const headers = await AuthUtils.getAuthHeaders();
  // ⚠️ MEASURED 2026-10-09 (the PO's deck BLOCKER): `getAuthHeaders()` sets
  // `Content-Type: application/json`, and the spread below used to carry it into a multipart
  // body — so FastAPI never parsed `file` and answered 422, which the journey showed as
  // "Not ready yet — Raisy needs file." The comment that said "NO Content-Type" was true of
  // the intent and false of the bytes. `kernelBotApi.apiCall` already does exactly this.
  delete (headers as Record<string, string>)["Content-Type"];
  const form = new FormData();
  form.append("file", file);

  try {
    const res = await fetch(`${API_URL}/api/v1/investor-outreach/attachment`, {
      method: "POST",
      headers,
      body: form,
    });
    const body: unknown = await res.json().catch(() => null);
    if (body && typeof body === "object" && "status" in body) {
      return body as ApiResponse<OutreachAttachment>;
    }
    return {
      status: false,
      statusCode: res.status,
      message: refusalMessage(body, res.status),
      data: null,
    };
  } catch (err) {
    // A transport failure used to propagate with no message at all — the founder picked a PDF
    // and nothing happened. The refusal path is the only path that may stay silent.
    return {
      status: false,
      statusCode: 0,
      message: `The deck could not be uploaded — ${(err as Error)?.message || "the request failed"}. The file is untouched; try again.`,
      data: null,
    };
  }
}

export async function listOutreachAttachments(): Promise<ApiResponse<OutreachAttachmentList>> {
  return apiCall("/api/v1/investor-outreach/attachments", { timeoutMs: 30_000 });
}

/**
 * @P279 T0200 — the raise's DECK, which is a different fact from "a file this user uploaded".
 *
 * `uploadOutreachAttachment` above already stores the bytes; these two bind that file to a
 * specific raise. The gap they close is named in the BE route itself: *"the file belongs to the
 * UPLOADER, which is what the schema supports"* — so before these existed, a deck was per-user
 * and no raise could name its own.
 */
export interface CampaignDeck {
  campaign_id: string;
  has_deck: boolean;
  deck: OutreachAttachment | null;
}

export async function getCampaignDeck(
  campaignId: string,
  entityId: string,
): Promise<ApiResponse<CampaignDeck>> {
  const id = encodeURIComponent(campaignId);
  const ent = encodeURIComponent(entityId);
  return apiCall(`/api/v1/investor-outreach/campaign/${id}/deck?entity_id=${ent}`, {
    timeoutMs: 20_000,
  });
}

/**
 * Bind an uploaded file to the raise. REPLACES rather than appends — a raise has one deck, so
 * calling this twice is "change my deck", never "add a second one".
 */
export async function attachCampaignDeck(
  campaignId: string,
  entityId: string,
  uploadedSourceId: string,
): Promise<ApiResponse<{ campaign_id: string; deck_source_id: string }>> {
  const id = encodeURIComponent(campaignId);
  return apiCall(`/api/v1/investor-outreach/campaign/${id}/deck`, {
    method: "POST",
    body: JSON.stringify({ entity_id: entityId, uploaded_source_id: uploadedSourceId }),
  });
}

/**
 * project092 / T0213 — send the founder her own email before the real batch.
 *
 * NOT a reachout: the backend writes no ledger row and debits no credit, so a test can never
 * appear in the outreach record or consume one of the free sends.
 */
export async function sendTestEmail(
  sender: SenderInfo,
  subject: string,
  body: string,
  attachmentIds: string[] = [],
): Promise<ApiResponse<{ sent: boolean; to: string }>> {
  return apiCall("/api/v1/investor-outreach/test-send", {
    method: "POST",
    body: JSON.stringify({
      sender,
      subject,
      body,
      attachment_ids: attachmentIds.length ? attachmentIds : null,
    }),
    timeoutMs: 60_000,
  });
}

export async function getInvestorActivity(
  ownerEntityId: string,
  investorEntityId: string,
  limit = 50,
): Promise<ApiResponse<InvestorActivityResponse>> {
  const params = new URLSearchParams({
    entity_id: ownerEntityId,
    investor_entity_id: investorEntityId,
    limit: String(limit),
  });
  return apiCall(`/api/v1/investor-outreach/investor-activity?${params.toString()}`, {
    timeoutMs: 30_000,
  });
}

/**
 * project092 / T0214 — the pre-send check.
 *
 * The backend runs the SAME compliance gate the real send enforces, plus deterministic content
 * checks, and returns them together with `not_measured` — the facts it cannot see (domain
 * reputation, a spam score). No number is invented; `sendable` is false when any check FAILS.
 */
export interface PreSendCheckItem {
  key: string;
  label: string;
  status: "pass" | "warn" | "fail";
  detail: string;
}

export interface PreSendCheckResponse {
  checks: PreSendCheckItem[];
  blockers: number;
  warnings: number;
  sendable: boolean;
  not_measured: Record<string, string>;
  summary: string;
}

export async function preSendCheck(
  sender: SenderInfo,
  opts: { subject?: string | null; body?: string | null; useCase?: string } = {},
): Promise<ApiResponse<PreSendCheckResponse>> {
  return apiCall("/api/v1/investor-outreach/presend-check", {
    method: "POST",
    body: JSON.stringify({
      sender,
      use_case: opts.useCase ?? "BD70.3",
      subject: opts.subject ?? null,
      body: opts.body ?? null,
    }),
  });
}
/**
 * D-064 — LLM per-column enrichment: re-fetches each investor's website and asks the
 * LLM to find ONLY the missing fields (stage_focus, check_size, sector_focus,
 * description, city, linkedin, contact_name, website). Email stays on Hunter.
 */
export async function enrichInvestorFields(
  investorEntityIds: string[],
  limit = 5,
  force = false,
): Promise<ApiResponse<{ results: Array<{
  entity_id: string;
  entity_name: string;
  filled: Record<string, { value: string; confidence: string; source: string }>;
  still_missing: string[];
  status: string;
}> }>> {
  return apiCall("/api/v1/investor-outreach/enrich-fields", {
    method: "POST",
    body: JSON.stringify({ investor_entity_ids: investorEntityIds, limit, force }),
    timeoutMs: 120_000,
  });
}

/**
 * D-088/D-086 — the QUEUED per-entity enrich farm (`enrich_investor`). This is the board's
 * readiness source: a recipient shows `ready / enriching… / needs warm intro` from these
 * per-entity job states (NOT the old inline `/enrich-fields`, which D-087 moved off the add path).
 */
export type InvestorEnrichJobState = "queued" | "in_progress" | "done" | "failed" | "skipped";

export interface InvestorEnrichJobStatus {
  id: string;
  batch_id: string | null;
  entity_kind: "person" | "company";
  status: InvestorEnrichJobState | string;
  attempt_count: number;
  result?: Record<string, unknown> | null;
  error_message?: string | null;
  updated_at?: string | null;
}

/** D-088 — enqueue investor entities (persons AND firms) into the queued enrich farm.
 *  One job per entity (idempotent); returns the batch (execution) handle the board polls. */
export async function enqueueInvestorEnrich(
  entityIds: string[],
  source = "board_enrich",
): Promise<
  ApiResponse<{
    batch_id: string | null;
    batch_number: number | null;
    enqueued: number;
    already_queued: number;
    missing: number;
    eta_seconds: number | null;
  }>
> {
  return apiCall("/api/v1/investor-outreach/enrich/enqueue", {
    method: "POST",
    body: JSON.stringify({ entity_ids: entityIds, source }),
    timeoutMs: 30_000,
  });
}

/** D-088/D-086 — latest enrich-job state for ONE entity (the readiness-gate input). */
export async function getInvestorEnrichStatus(
  entityId: string,
): Promise<ApiResponse<{ entity_id: string; job: InvestorEnrichJobStatus | null }>> {
  return apiCall("/api/v1/investor-outreach/enrich/status", {
    method: "POST",
    body: JSON.stringify({ entity_id: entityId }),
    timeoutMs: 30_000,
  });
}

/**
 * D-086 — Draft the sender wording block (one-line pitch / round description / LIA) via the
 * registered Investor Outreach Copy Drafter bot (kernel-fed: BD1 persona + BD196 reference
 * + the entity's BD90 raise-profile + declared round as BD0 input). Logged to bot_call_logs.
 */
export interface PrepareSenderDraft {
  pitch: string;
  raising: string;
  lia: string;
  bot_key?: string;
  mode?: string;
  kernel_count?: number;
}

export async function prepareSender(
  entityId: string,
  round: {
    raising_stage?: string;
    raising_amount?: string;
    check_size?: string;
    sector?: string;
    target_geography?: string;
  } = {},
): Promise<ApiResponse<{ sender: PrepareSenderDraft }>> {
  return apiCall("/api/v1/investor-outreach/prepare-sender", {
    method: "POST",
    body: JSON.stringify({ entity_id: entityId, ...round }),
    timeoutMs: 120_000,
  });
}

/** D-079 — one candidate investor returned by /resolve (pick the right one before adding). */
export interface InvestorResolveCandidate {
  website: string;
  label: string;
  kind: "url" | "db" | "fund" | "person";
  source: "url" | "db" | "principal" | "resolver" | "hunter_companies" | "hunter_people";
  entity_id?: string;
  /** D-082 — entity identity (MR1): persons AND firms are Entities. */
  entity_name?: string;
  entity_type?: "company" | "person";
  affiliation?: string;
  role?: string;
}

/** D-079 — resolve a fund name / person name / URL to candidate investors to add. */
export async function resolveInvestor(
  query: string,
): Promise<ApiResponse<{ candidates: InvestorResolveCandidate[] }>> {
  return apiCall("/api/v1/investor-outreach/resolve", {
    method: "POST",
    body: JSON.stringify({ query }),
    timeoutMs: 90_000,
  });
}

/** D-064 — add an investor (firm) to the database by NAME or website URL (entity + BD90.6).
 *  D-082 — `raisingEntityId` links it to the raise via an `investor` relationship edge. */
export async function findAndAddInvestor(
  input: string,
  source = "user_scrape",
  raisingEntityId?: string | null,
): Promise<ApiResponse<{
  status: "created" | "exists" | "fetch_failed" | "identify_failed" | "not_found";
  entity_id?: string;
  entity_name?: string;
  website?: string;
  resolved_from_name?: string;
  filled?: Record<string, { value: string; confidence: string; source: string }>;
  still_missing?: string[];
}>> {
  // A URL → scrape it; anything else (a fund name) is resolved server-side (DB + Hunter Discover).
  const looksLikeUrl = /^https?:\/\//i.test(input) || (input.includes(".") && !input.includes(" "));
  const payload: Record<string, unknown> = looksLikeUrl ? { url: input, source } : { name: input, source };
  if (raisingEntityId) payload.raising_entity_id = raisingEntityId;
  return apiCall("/api/v1/investor-outreach/find-and-add", {
    method: "POST",
    body: JSON.stringify(payload),
    timeoutMs: 120_000,
  });
}

/** D-082 — add a PERSON as a first-class investor Entity (MR1). Creates a prospect person
 *  Entity + employee link to their affiliation firm + investor edge to the raising entity. */
export async function addInvestorPerson(payload: {
  entity_name: string;
  affiliation?: string;
  role?: string;
  website?: string;
  raising_entity_id?: string | null;
}): Promise<ApiResponse<{
  status: "created" | "exists" | "invalid";
  entity_id?: string;
  entity_name?: string;
  firm_entity_id?: string | null;
  reason?: string;
}>> {
  return apiCall("/api/v1/investor-outreach/add-person", {
    method: "POST",
    body: JSON.stringify(payload),
    timeoutMs: 90_000,
  });
}
export async function sendInvestorOutreach(
  request: OutreachSendRequest,
): Promise<ApiResponse<OutreachSendResponse>> {
  return apiCall("/api/v1/investor-outreach/send", {
    method: "POST",
    body: JSON.stringify(request),
    // ⚠️ 60 s WAS A CLIENT-SIDE LIE (measured on DEV 2026-09-24, kanban t_339a2477). This ONE
    // function serves BOTH acts on the Email Board: the dry-run ASSEMBLE (`dry_run: true`,
    // `personalize: true`) and the REAL SEND — and the endpoint runs **one writer-bot LLM call per
    // recipient** (up to `limit` = 10) before the compliance gate and the dispatch. 60 s was fine
    // when the writer answered fast (a measured 35 s assemble) and became a failure the moment it
    // did not: the request was aborted at exactly 60 s and the board rendered **"signal is aborted
    // without reason"** over the candidates step, with 0 console errors and 0 HTTP>=400 — a
    // client-side abort is invisible to every counter this product has.
    //
    // WHY A BIGGER NUMBER IS THE HONEST FIX, NOT A LONGER SPINNER: `controller.abort()` cancels the
    // RESPONSE, never the WORK. The server carries on assembling and, on a real send, carries on
    // DISPATCHING REAL EMAILS. So a 60 s abort on the send is the worst shape there is — the founder
    // is shown a failure while the emails leave and no receipt is ever read back to him. The bound
    // is derived from the endpoint's own shape (10 recipients × one LLM draft each, plus the gate
    // and the pipe), and the file's own precedent for a similar fan-out is 120 s (enrich above).
    timeoutMs: 300_000,
  });
}

/**
 * D-064 / MR21 — the email acquisition lane. Runs Hunter.io discovery + verification on
 * the selected investors and writes the results as provenance-stamped BD90.6 kernel
 * versions (never bare row edits). Idempotent — already-enriched funds are skipped.
 * Best-effort: the caller must proceed to assemble regardless of how many were found.
 */
export async function enrichInvestorEmails(
  investorEntityIds: string[],
  limit?: number,
): Promise<ApiResponse<{ enriched?: number; skipped?: number; failed?: number; message?: string }>> {
  return apiCall("/api/v1/investor-funnel/enrich", {
    method: "POST",
    body: JSON.stringify({
      investor_entity_ids: investorEntityIds,
      limit: limit ?? investorEntityIds.length,
    }),
    timeoutMs: 120_000,
  });
}

// ── PROJECT-107 / T0400 — NL Investor Search ─────────────────────────────
// Mirrors the backend `_build_investor_profile` shape returned by
// POST /api/v1/investor-funnel/nl-search.
export interface NlInvestorProfile {
  entity_id?: string;
  entitykey?: string;
  name?: string;
  fund?: string;
  entity_type?: string;
  entity_type_sub?: string;
  location?: string;
  focus?: string;
  sector_focus?: string;
  stage_focus?: string;
  country?: string;
  region?: string;
  city?: string;
  check_size?: string;
  website?: string;
  email_status?: string;
}

export interface NlSearchUnderstood {
  geography?: string | null;
  investor_type?: string | null;
  sector?: string | null;
  stage?: string | null;
  confidence?: number;
}

export interface NlSearchResult {
  status?: "ok" | "error";
  source?: "db" | "db_thin";
  investors?: NlInvestorProfile[];
  message?: string;
  query_understood?: NlSearchUnderstood;
  coverage?: "sufficient" | "thin";
  confidence_note?: string | null;
  acquisition?: { available?: boolean; batch_cap?: number; ready?: boolean; message?: string };
}

export async function nlInvestorSearch(query: string): Promise<ApiResponse<NlSearchResult>> {
  return apiCall("/api/v1/investor-funnel/nl-search", {
    method: "POST",
    body: JSON.stringify({ query }),
    timeoutMs: 60_000,
  });
}

// ── PROJECT-107 / D-004 — instant faceted search (no LLM) + acquisition queue ──
export interface FacetFilters {
  geography?: string;
  investor_type?: string;
  sector?: string;
  stage?: string;
}

export async function facetedSearch(filters: FacetFilters): Promise<ApiResponse<NlSearchResult>> {
  return apiCall("/api/v1/investor-funnel/search", {
    method: "POST",
    body: JSON.stringify(filters),
  });
}

export interface AcquisitionEnqueueResult {
  batch_id?: string;
  batch_number?: number | null;
  enqueued?: number;
  requested?: number;
  capped?: boolean;
  eta_seconds?: number | null;
}

export async function acquisitionEnqueue(
  urls: string[],
  source?: string,
): Promise<ApiResponse<AcquisitionEnqueueResult>> {
  return apiCall("/api/v1/investor-funnel/acquisition/enqueue", {
    method: "POST",
    body: JSON.stringify({ urls, source }),
  });
}

export interface AcquisitionBatch {
  batch_id?: string;
  batch_number?: number | null;
  user_id?: string | null;
  source?: string;
  status?: "queued" | "running" | "completed" | "partial" | "failed";
  total_urls?: number;
  enqueued_count?: number;
  fetched_count?: number;
  parsed_count?: number;
  failed_count?: number;
  imported_count?: number;
  eta_seconds?: number | null;
  created_at?: string;
  started_at?: string | null;
  completed_at?: string | null;
}

export async function acquisitionBatches(
  status?: string,
  limit = 50,
): Promise<ApiResponse<{ batches: AcquisitionBatch[]; total: number }>> {
  const params = new URLSearchParams();
  if (status) params.set("status", status);
  params.set("limit", String(limit));
  return apiCall(`/api/v1/investor-funnel/acquisition/batches?${params.toString()}`, { method: "GET" });
}

export interface AcquisitionJob {
  target_url?: string;
  status?: string;
  attempt_count?: number;
  error_message?: string | null;
  parsed_names?: unknown;
  created_at?: string;
  updated_at?: string;
}

export async function acquisitionBatchDetail(
  batchId: string,
): Promise<ApiResponse<{ batch: AcquisitionBatch; jobs: AcquisitionJob[] }>> {
  return apiCall(`/api/v1/investor-funnel/acquisition/batches/${batchId}`, { method: "GET" });
}

// ── @P092 T0212 — SCHEDULED SENDS ───────────────────────────────────────────────────────────────
// The shapes are the BE's own response (verified against the batch's captured output, not guessed):
// the moment travels THREE ways and all three are rendered — `send_at` is when it happens,
// `send_at_local` + `send_at_zone` are what the founder meant, and `send_at_cet` is the BE's own
// reading of the same instant (D-013: CET is the normalisation standard, never the screen's label).

/** How a layer's zone became known. `declared` is knowledge; the other two are honest approximations. */
export type TimezoneLayerSource = "declared" | "derived" | "fallback";

export interface TimezoneLayer {
  role: "browser" | "entity" | "company" | "recipient";
  /** Null when the zone cannot be named — shown empty rather than guessed. */
  zone: string | null;
  source: TimezoneLayerSource;
  entity_id?: string;
  entity_type?: string | null;
}

export interface OutreachSchedule {
  schedule_id: string;
  owner_entity_id: string;
  campaign_id?: string | null;
  investor_entity_id?: string | null;
  draft_call_id?: string | null;
  /** The instant, ISO with offset — what every layer renders. */
  send_at: string;
  /** The wall clock the founder typed, no offset. */
  send_at_local: string;
  send_at_zone: string;
  /** The same instant read in CET, so the BE's own standard is visible rather than implied. */
  send_at_cet?: string | null;
  status: "pending" | "sent" | "cancelled" | "failed";
  timezone_layers: TimezoneLayer[];
  recipient_country?: string | null;
  recipient_zone_estimate?: string | null;
  communication_id?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
}

export interface ScheduleSendPayload {
  entity_id: string;
  /** The wall clock she typed — NO offset: the zone below decides the instant, never the clock. */
  send_at_local: string;
  /** IANA zone she meant it in — e.g. Europe/Berlin. */
  send_at_zone: string;
  /** The VIEWER's zone. Sent because the browser is the only thing that knows it. */
  browser_zone?: string;
  campaign_id?: string;
  investor_entity_id?: string;
  recipient_country?: string;
  recipient_zone?: string;
  draft_call_id?: string;
}

/** Record a future moment on an outreach send. */
export async function createOutreachSchedule(
  payload: ScheduleSendPayload,
): Promise<ApiResponse<OutreachSchedule>> {
  return apiCall("/api/v1/investor-outreach/schedule", { method: "POST", body: JSON.stringify(payload) });
}

/** The founder's OWN schedules, oldest moment first. */
export async function listOutreachSchedules(
  entityId: string,
  status?: string,
): Promise<ApiResponse<{ schedules: OutreachSchedule[] }>> {
  const params = new URLSearchParams({ entity_id: entityId });
  if (status) params.set("status", status);
  return apiCall(`/api/v1/investor-outreach/schedules?${params.toString()}`, { method: "GET" });
}

/** Withdraw a pending schedule. The id travels in the PATH, so the QA batch can chain it. */
export async function cancelOutreachSchedule(
  entityId: string,
  scheduleId: string,
): Promise<ApiResponse<{ cancelled: boolean; schedule_id?: string }>> {
  return apiCall(`/api/v1/investor-outreach/schedule/${scheduleId}/cancel`, {
    method: "POST",
    body: JSON.stringify({ entity_id: entityId }),
  });
}
